import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Divider,
    Alert
} from '@mui/material';
import { formatTimestamp, getStatusColor } from '../../../utils/formatters';

const MessageDetailsModal = ({ message, open, onClose }) => {
    if (!message) return null;

    const formatContent = (content) => {
        if (!content) return 'No content';

        try {
            // Try to parse as JSON for better formatting
            return JSON.stringify(content, null, 2);
        } catch {
            console.warn('Message content is not valid JSON');

            // If not JSON, return as-is
            return content;
        }
    };

    return (
        <Dialog
            open={ open }
            onClose={ onClose }
            maxWidth="md"
            fullWidth
            scroll="paper"
        >
            <DialogTitle>
                Message Details
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={ { mb: 2 } }>
                    <Typography variant="h6" gutterBottom>
                        Basic Information
                    </Typography>

                    <Box sx={ { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 } }>
                        <Typography><strong>ID:</strong> { message.id }</Typography>
                        <Typography><strong>Type:</strong> { message.type }</Typography>
                        <Typography><strong>Recipient:</strong> { message.recipient }</Typography>
                        <Typography component="div">
                            <strong>Status:</strong>

                            <Chip
                                label={ message.status }
                                color={ getStatusColor(message.status) }
                                size="small"
                                sx={ { ml: 1 } }
                            />
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={ { my: 2 } } />

                <Box sx={ { mb: 2 } }>
                    <Typography variant="h6" gutterBottom>
                        Timestamps
                    </Typography>

                    <Box sx={ { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 } }>
                        <Typography><strong>Created:</strong> { formatTimestamp(message.createdAt) }</Typography>
                        <Typography><strong>Scheduled:</strong> { formatTimestamp(message.scheduledTime) }</Typography>
                        <Typography><strong>Last Attempt:</strong> { formatTimestamp(message.lastAttemptAt) }</Typography>
                        <Typography><strong>Sent:</strong> { formatTimestamp(message.sentAt) }</Typography>
                    </Box>
                </Box>

                <Divider sx={ { my: 2 } } />

                <Box sx={ { mb: 2 } }>
                    <Typography variant="h6" gutterBottom>
                        Retry Information
                    </Typography>

                    <Typography><strong>Attempts:</strong> { message.attempts || 0 }</Typography>
                </Box>

                { message.error && (
                    <>
                        <Divider sx={ { my: 2 } } />
                        <Box sx={ { mb: 2 } }>
                            <Typography variant="h6" gutterBottom>
                                Error Details
                            </Typography>

                            <Alert severity="error" variant='outlined'>
                                <Typography variant="body2" component="pre" sx={ { whiteSpace: 'pre-wrap' } }>
                                    { message.error }
                                </Typography>
                            </Alert>
                        </Box>
                    </>
                ) }

                <Divider sx={ { my: 2 } } />

                <Box>
                    <Typography variant="h6" gutterBottom>
                        Message Content
                    </Typography>
                    
                    <Box sx={ {
                        padding: 2,
                        backgroundColor: '#656565ff',
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        maxHeight: 300,
                        overflow: 'auto'
                    } }>
                        <Typography
                            variant="body2"
                            component="pre"
                            sx={ {
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                margin: 0
                            } }
                        >
                            { formatContent(message.content) }
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={ onClose }>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default MessageDetailsModal;
